import { Field, FieldArray, Formik, FormikHelpers as FormikActions } from "formik";
import React from "react";
import { Button } from "~/components/Button";
import { Trash2 } from "lucide";
import { LucideIcon } from "~/components/LucideIcon";
import { Form, FormControls, FormRow, ButtonField, CheckboxField, FormTitle, hasErrors, InputField, IntentText } from "~/components/Form";
import { IModalComponent, IModalComponentClose } from "~/components/Modal";
import { TabsWithBody } from "~/components/Tabs";
import { useAbility } from "~/lib/ability";
import { useConfirm } from "~/components/Dialogs";
import { renderBoolean } from "~/lib/helpers";
import { Intent } from "~/lib/intent";
import { SourcesAbility } from "~/modules/Sources/ability";
import {
  ILimitedSelfServeSource,
  LimitedSelfServeSource,
  defaultLimitedSelfServeSource,
} from "~/modules/Sources/models/LimitedSelfServeSource";
import { defaultPlacement } from "~/modules/Sources/models/Placement";
import { createPlacement, updatePlacement, deleteSellerPlacement } from "~/modules/Sources/api";
import { usePlacements } from "~/modules/Sources/usePlacements";
import { PlacementsSearch } from "~/modules/Sources/components/PlacementsSearch";
import { Pagination } from "~/components/Pagination";
import { PlacementForm } from "~/modules/Sources/forms/PlacementForm";
import { ImportPlacementsButton } from "~/modules/Sources/components/ImportPlacementsButton";
import { useModals } from "~/components/Modal";
import { CopyableText } from "~/components/CopyableText";
import { Table } from "~/components/Table";
import { defaultSourceDomain } from "~/modules/Sources/models/IDomains";
import style from "~/modules/Sources/forms/index.module.scss";

interface SelfServeSourceFormProps {
  source?: ILimitedSelfServeSource;
  onSubmit?: (values: ILimitedSelfServeSource, formikActions: FormikActions<ILimitedSelfServeSource>, close: IModalComponentClose) => void;
}

export const SelfServeSourceForm: IModalComponent<SelfServeSourceFormProps> = (props) => {
  const ability = useAbility(SourcesAbility);
  const confirm = useConfirm();

  const sourceId = props.source?.id;
  const placements = usePlacements(sourceId);

  const modals = useModals();

  const createPlacementModal = modals.create(PlacementForm, {
    onSubmit: (newPlacement, actions, close) => {
      createPlacement(sourceId, newPlacement)
        .then(() => {
          close();
          placements.reload();
        })
        .catch((error) => {
          actions.setStatus(error.message || "Error creating placement");
          actions.setSubmitting(false);
        });
    },
  });

  const editPlacementModal = modals.create(PlacementForm, {
    onSubmit: (updatedPlacement, actions, close) => {
      updatePlacement(sourceId, updatedPlacement)
        .then(() => {
          close();
          placements.reload();
        })
        .catch((error) => {
          actions.setStatus(error.message || "Error updating placement");
          actions.setSubmitting(false);
        });
    },
  });

  return (
    <Formik
      initialValues={{ ...defaultLimitedSelfServeSource, ...props.source }}
      validationSchema={LimitedSelfServeSource}
      onSubmit={(source, actions) => props.onSubmit(source, actions, props.close)}
    >
      {(formik) => {
        const { values: source, status, isSubmitting } = formik;

        const tabs = [];

        if (ability.isSelfServe()) {
          tabs.push({
            title: "Domains",
            intent: hasErrors(formik, ["domains"]) && Intent.DANGER,
            children: (
              <FieldArray
                name="domains"
                render={(arrayHelpers) => (
                  <div>
                    {(source.domains ?? []).map((setting, index) => (
                      <FormRow noBorder noMargin key={index}>
                        <div className={style.domainsForm}>
                          <Field
                            name={`domains.${index}.domain`}
                            label="Domain"
                            className={style.domainsInput}
                            options={{
                              placeholder: "Domain",
                              disabled: isSubmitting,
                            }}
                            component={InputField}
                          />
                          <Field
                            name={`domains.${index}.bundles`}
                            label="Bundles"
                            className={style.domainsInput}
                            options={{
                              placeholder: "Bundles",
                              disabled: isSubmitting,
                            }}
                            component={InputField}
                          />
                          <Field
                            name={`domains.${index}.isDefault`}
                            label="Default"
                            className={style.domainsCheckbox}
                            options={{
                              placeholder: "Is default",
                              disabled: isSubmitting,
                            }}
                            component={CheckboxField}
                          />
                          <Field
                            name={"delete"}
                            className={style.domainsBtn}
                            options={{
                              onClick: () =>
                                confirm("Delete domain?", {
                                  onOk: () => arrayHelpers.remove(index),
                                  icon: <LucideIcon icon={Trash2} size={20} />,
                                  intent: Intent.DANGER,
                                }),
                              icon: <i className="fa fa-trash" />,
                              intent: Intent.DANGER,
                            }}
                            component={ButtonField}
                          />
                        </div>
                      </FormRow>
                    ))}
                    <div className={style.addDomainBtn}>
                      <Button
                        type="button"
                        disabled={isSubmitting}
                        intent={Intent.PRIMARY}
                        onClick={() => {
                          arrayHelpers.push(defaultSourceDomain);
                        }}
                      >
                        Add domain
                      </Button>
                    </div>
                  </div>
                )}
              />
            ),
          });
        }

        tabs.push({
          title: "Placements",
          children: (
            <div className={style.placementsForm}>
              <PlacementsSearch placements={placements} className={style.placementsSearch} />
              <Table
                header
                records={placements.items}
                columns={[
                  { header: "Name", field: "name", cellWrap: "break-word", headerAttrs: { className: style.nameCell }, cellAttrs: { className: style.nameCell } },
                  { header: "ID", cellWrap: "break-word", headerAttrs: { className: style.idCell }, cellAttrs: { className: style.idCell }, cell: (placement) => <CopyableText>{placement.id || ""}</CopyableText> },
                  { header: "Platform", field: "platform" },
                  { header: "Bundle", field: "bundle", cellWrap: "break-word", headerAttrs: { className: style.bundleCell }, cellAttrs: { className: style.bundleCell } },
                  { header: "Ad Type", field: "adType" },
                  { header: "Placement Type", field: "placementType" },
                  { header: "HVA", cell: (placement) => renderBoolean(placement.hva) },
                  { header: "Pricefloor", field: "pricefloor" },
                  { header: "MAX Ad Unit ID", field: "maxAdUnitId", cellWrap: "break-word" },
                  { header: "GPID", field: "gpid", cellWrap: "break-word" },
                  {
                    header: "",
                    small: true,
                    cell: (placement) => (
                      <div className={style.placementActions}>
                        <Button
                          small
                          responsive
                          leftIcon={<i className="fa fa-edit" />}
                          onClick={() => editPlacementModal.show({ placement })}
                        />
                        <Button
                          small
                          responsive
                          leftIcon={<i className="fa fa-trash" />}
                          intent={Intent.DANGER}
                          onClick={() =>
                            confirm("Delete placement?", {
                              onOk: () =>
                                deleteSellerPlacement(placement.id, sourceId).then(() =>
                                  placements.reload(),
                                ),
                            })
                          }
                        />
                      </div>
                    ),
                  },
                ]}
              />

              <Pagination
                padding={4}
                limit={placements.pageSize}
                offset={placements.pageNo * placements.pageSize}
                total={placements.count}
                onChange={placements.changePage}
                limits={[25, 50, 100, 200]}
              />

              <FormControls>
                <ImportPlacementsButton onImported={placements.reload} />
                <Button
                  type="button"
                  intent={Intent.PRIMARY}
                  onClick={() =>
                    createPlacementModal.show({
                      placement: defaultPlacement,
                    })
                  }
                >
                  Add Placement
                </Button>
              </FormControls>
            </div>
          ),
        });

        return (
          <Form>
            <FormTitle>Edit Source</FormTitle>
            {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}

            <TabsWithBody tabs={tabs} />

            <FormControls>
              <Button disabled={isSubmitting} onClick={props.close}>
                {ability.canOnlyView() ? "Close" : "Cancel"}
              </Button>
              {!ability.canOnlyView() && ability.isSelfServe() && (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  intent={Intent.DANGER}
                  rightIcon={isSubmitting && <i className="fa fa-spinner fa-spin" />}
                >
                  Save
                </Button>
              )}
            </FormControls>
          </Form>
        );
      }}
    </Formik>
  );
};
